import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import COUNTRIES from '@/data/countries';

const ConsultationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/consultations', {
        ...formData,
        created_at: new Date().toISOString(),
      });
      
      toast.success('درخواست مشاوره شما ثبت شد. به زودی با شما تماس خواهیم گرفت.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        country: ''
      });
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      toast.error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border border-[#D4AF37]/30 rounded-2xl shadow-2xl animate-slideUp">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="text-white" size={20} />
        </button>

        {/* Header */}
        <div className="p-8 pb-6 text-center border-b border-white/10">
          <h2 className="text-3xl font-light text-white mb-2">
            دریافت <span className="text-[#D4AF37]">مشاوره رایگان</span>
          </h2>
          <p className="text-zinc-400 text-sm">
            فرم زیر را تکمیل کنید تا کارشناسان ما با شما تماس بگیرند
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">نام و نام خانوادگی *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                placeholder="نام خود را وارد کنید"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-2">شماره تماس *</label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                placeholder="09121234567"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-2">ایمیل *</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="email@example.com"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">نوع خدمت *</label>
              <select
                name="service"
                required
                value={formData.service}
                onChange={handleChange}
                className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-[#D4AF37] font-medium focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="" className="bg-[#1a1a1a] text-white">انتخاب کنید</option>
                <option value="education" className="bg-[#1a1a1a] text-white">مشاوره تحصیلی</option>
                <option value="visa" className="bg-[#1a1a1a] text-white">اخذ ویزا</option>
                <option value="work" className="bg-[#1a1a1a] text-white">مهاجرت کاری</option>
                <option value="investment" className="bg-[#1a1a1a] text-white">مهاجرت سرمایه‌گذاری</option>
                <option value="other" className="bg-[#1a1a1a] text-white">سایر خدمات</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-2">کشور موردنظر</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-[#D4AF37] font-medium focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="" className="bg-[#1a1a1a] text-white">انتخاب کنید</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country} className="bg-[#1a1a1a] text-white">
                    {country}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-2">پیام شما</label>
            <textarea
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="توضیحات تکمیلی..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium py-4 rounded-lg hover:shadow-lg hover:shadow-[#D4AF37]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'در حال ارسال...' : 'ارسال درخواست مشاوره'}
          </button>
        </form>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ConsultationModal;
